import { useEffect, useState } from 'react';
import { Settings } from 'lucide-react';
import { useAuth } from './app/auth.ts';
import { useT } from './app/preferences.ts';
import { currentLocale, matchRoute, usePathname, type Route } from './app/router.ts';
import { Link } from './components/Link.tsx';
import { LogoMark } from './components/Brand.tsx';
import { AccountButton } from './components/AccountButton.tsx';
import { SettingsDialog } from './components/SettingsDialog.tsx';
import { TestAdDialog } from './components/TestAdDialog.tsx';
import { ComputerGamePage } from './pages/ComputerGamePage.tsx';
import { ComputerSetupPage } from './pages/ComputerSetupPage.tsx';
import { FriendPage } from './pages/FriendPage.tsx';
import { HomePage } from './pages/HomePage.tsx';
import { GiftsPage } from './pages/GiftsPage.tsx';
import { LeaderboardPage } from './pages/LeaderboardPage.tsx';
import { MarketPage } from './pages/MarketPage.tsx';
import { OnlinePage } from './pages/OnlinePage.tsx';
import { MyProfilePage, ProfilePage } from './pages/ProfilePage.tsx';
import { WelcomePage } from './pages/WelcomePage.tsx';
import { RoomPage } from './pages/RoomPage.tsx';
import { InfoPage } from './pages/InfoPage.tsx';
import { INFO_PAGES } from './content/infoPages.ts';

export function App() {
  const t = useT();
  const { needsUsername, user } = useAuth();
  const signedIn = Boolean(user?.username);
  const pathname = usePathname();
  const route = matchRoute(pathname);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    document.title = pageTitle(route, t);
  }, [route, t]);

  return (
    <div className="app">
      <header className="app-header">
        <Link href="/" className="brand" aria-label="Chessrokko">
          <LogoMark />
          <span>Chessrokko</span>
        </Link>
        {/* On phones a signed-in player sees only their avatar; settings move into its panel. */}
        <div className={`header-actions${signedIn ? ' signed-in' : ''}`}>
          <AccountButton onOpenSettings={() => setSettingsOpen(true)} />
          <button type="button" className="btn btn-sm settings-btn" onClick={() => setSettingsOpen(true)} aria-label={t('nav.settings')}>
            <Settings size={22} />
            <span className="header-label">{t('nav.settings')}</span>
          </button>
        </div>
      </header>

      <main className="app-main">
        {/* Keyed by path so every page change replays the bounce-in animation. */}
        <div className="page" key={needsUsername ? 'welcome' : pathname}>
          {/* A new account must pick a username before anything else. */}
          {needsUsername ? <WelcomePage /> : <Page route={route} />}
        </div>
      </main>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <TestAdDialog />
    </div>
  );
}

function Page({ route }: { route: Route }) {
  switch (route.name) {
    case 'home':
      return <HomePage />;
    case 'computer':
      return <ComputerSetupPage />;
    case 'computerGame':
      return <ComputerGamePage />;
    case 'friend':
      return <FriendPage />;
    case 'online':
      return <OnlinePage />;
    case 'leaderboard':
      return <LeaderboardPage />;
    case 'market':
      return <MarketPage />;
    case 'gifts':
      return <GiftsPage />;
    case 'me':
      return <MyProfilePage />;
    case 'profile':
      return <ProfilePage key={route.username} username={route.username} />;
    case 'room':
      return <RoomPage key={route.code} code={route.code} />;
    case 'info':
      return <InfoPage key={route.page} id={route.page} />;
    case 'notFound':
      return <NotFound />;
  }
}

function NotFound() {
  const t = useT();
  return (
    <div className="center-state pop-in">
      <span className="result-icon emoji" aria-hidden="true">
        🔍
      </span>
      <h1 className="page-title">{t('notFound.title')}</h1>
      <p>{t('notFound.desc')}</p>
      <Link href="/" className="btn btn-primary btn-lg">
        {t('room.home')}
      </Link>
    </div>
  );
}

function pageTitle(route: Route, t: ReturnType<typeof useT>): string {
  switch (route.name) {
    case 'info':
      return `${INFO_PAGES[route.page][currentLocale()].title} · Chessrokko`;
    case 'computer':
    case 'computerGame':
      return `${t('computer.title')} · Chessrokko`;
    case 'friend':
      return `${t('friend.title')} · Chessrokko`;
    case 'online':
      return `${t('online.title')} · Chessrokko`;
    case 'leaderboard':
      return `${t('board.title')} · Chessrokko`;
    case 'market':
      return `${t('shop.title')} · Chessrokko`;
    case 'gifts':
      return `${t('gifts.title')} · Chessrokko`;
    case 'me':
      return `${t('nav.profile')} · Chessrokko`;
    case 'profile':
      return `${route.username} · Chessrokko`;
    case 'room':
      return `${route.code} · Chessrokko`;
    case 'notFound':
      return `${t('notFound.title')} · Chessrokko`;
    default:
      return `Chessrokko · ${t('app.tagline')}`;
  }
}
